import React, { useState, useContext, useEffect, useImperativeHandle, useRef } from "react";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { AccountContext } from "context/account";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiTagInput from "components/SuiTagInput";

import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import NumbersIcon from "@mui/icons-material/Numbers";
import EditIcon from "@mui/icons-material/Edit";
import UploadIcon from "@mui/icons-material/Upload";
import DragHandleIcon from "@mui/icons-material/DragHandle";

import toast from "react-hot-toast";

import api from "services/api";
import moment from "moment";
import { nanoid } from "nanoid";

import Tooltip from "@mui/material/Tooltip";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";

const EditColumnsModal = ({ visible, handleClose, handleOk, columns = [] }) => {
  const [columnList, setColumnList] = useState(columns);

  const handleDrop2 = (droppedItem) => {
    if (!droppedItem.destination) return;
    var updatedList = [...columnList];
    const [reorderedItem] = updatedList.splice(droppedItem.source.index, 1);
    updatedList.splice(droppedItem.destination.index, 0, reorderedItem);
    setColumnList(updatedList);
  };

  return (
    <Dialog
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "720px",
            height: "700px",
          },
        },
      }}
      open={visible}
      maxWidth={"lg"}
      onClose={() => setEditColumnDialogVisible(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">
        Edit Columns{" "}
        <SuiTypography variant="body2" color="dark">
          Users can rearrange the order of table columns by dragging and dropping them.
        </SuiTypography>
      </DialogTitle>

      <DialogContent>
        <DragDropContext onDragEnd={handleDrop2}>
          <Droppable droppableId="column-container" direction="vertical">
            {(provided) => (
              <div
                style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
                {...provided.droppableProps}
                ref={provided.innerRef}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                {columnList.map((column, index) => (
                  <Draggable
                    id={column.field}
                    key={column.field}
                    draggableId={column.field}
                    index={index}
                    isDragDisabled={column.field == "Code1"}
                  >
                    {(provided) => {
                      provided.draggableProps.style = {
                        ...provided.draggableProps.style,
                        width: "700px",
                      };
                      return (
                        <div
                          ref={provided.innerRef}
                          {...provided.dragHandleProps}
                          {...provided.draggableProps}
                        >
                          <SuiBox
                            sx={{ width: "700px", px: 1 }}
                            display="flex"
                            alignItems="center"
                            justifyContent="flex-start"
                          >
                            <DragHandleIcon
                              fontSize="small"
                              visibility={column.field == "Code1" ? "hidden" : ""}
                            />
                            <SuiTypography
                              sx={{
                                textAlign: "right",
                                width: "100px",
                                minWidth: "100px",
                                whiteSpace: "nowrap",
                              }}
                              variant="body1"
                            >
                              {index + 1}. Column
                            </SuiTypography>

                            <>
                              <SuiBox sx={{ width: "400px", margin: "5px 20px" }}>
                                <SuiInput
                                  autoFocus={column.headerName === ""}
                                  value={column.headerName}
                                  onChange={(e) => {
                                    const newDatas = columnList.map((c) => {
                                      if (column.field === c.field)
                                        return {
                                          ...c,
                                          headerName: e.target.value,
                                        };
                                      else return c;
                                    });
                                    setColumnList(newDatas);
                                  }}
                                />
                              </SuiBox>
                              <IconButton
                                disabled={column.field == "Code"}
                                size="medium"
                                onClick={() => {
                                  const newDatas = columnList.filter((c) => {
                                    return column.field !== c.field;
                                  });
                                  setColumnList(newDatas);
                                }}
                              >
                                <Icon
                                  fontSize="medium"
                                  className="border-none hover:bg-transparent"
                                >
                                  clear
                                </Icon>
                              </IconButton>
                            </>
                          </SuiBox>
                        </div>
                      );
                    }}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </DialogContent>
      <DialogActions>
        <SuiButton
          onClick={() => {
            setColumnList([
              ...columnList,
              { field: nanoid(), headerName: "", created_at: new Date() },
            ]);
          }}
          color={"dark"}
          variant="gradient"
        >
          Add Column
        </SuiButton>
        <div style={{ flex: "1 0 0" }} />
        <SuiButton onClick={handleClose} color={"secondary"} variant="gradient">
          Cancel
        </SuiButton>
        <SuiButton
          onClick={() => {
            handleOk(columnList);
          }}
          color={"dark"}
          variant="gradient"
        >
          Update
        </SuiButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditColumnsModal;
